import { Timestamp } from "firebase/firestore";
import { weightClasses, weightClassesName } from "../constants";
export const capitalizeFirstLetter = (val) => {
    return String(val).charAt(0).toUpperCase() + String(val).slice(1);
};

export const getYouTubeVideoThumbnailById = (id) => {
    return `https://img.youtube.com/vi/${id}/0.jpg`;
};

export function getYouTubeVideoId(url) {
    try {
        // Handle protocol-less URLs
        if (!/^https?:\/\//i.test(url)) {
            url = "https://" + url;
        }

        const parsedUrl = new URL(url);
        const host = parsedUrl.hostname.replace("www.", "");
        const path = parsedUrl.pathname;
        const params = parsedUrl.searchParams;

        // Check for youtu.be (shortened links)
        if (host === "youtu.be") {
            return path.split("/")[1] || null;
        }

        // Check for YouTube domains
        if (host.includes("youtube.")) {
            // Handle path patterns: /embed/ /live/ /shorts/ /v/
            const pathPatterns = ["/embed/", "/live/", "/shorts/", "/v/"];
            for (const pattern of pathPatterns) {
                if (path.startsWith(pattern)) {
                    return path.split("/")[2] || null;
                }
            }

            // Handle watch links
            if (path === "/watch" && params.has("v")) {
                return params.get("v");
            }

            // Fallback: check 'v' parameter in any path
            if (params.has("v")) {
                return params.get("v");
            }
        }

        return null;
    } catch (e) {
        return null;
    }
}

/**
 * Normalize liveStreamUrl (string or array) to an array of non-empty strings.
 * Handles legacy single string and new array format for backward compatibility.
 * @param {string | string[] | null | undefined} value - liveStreamUrl from event
 * @returns {string[]}
 */
export function getLiveStreamUrls(value) {
    if (Array.isArray(value)) {
        return value.filter((s) => s != null && String(s).trim() !== "");
    }
    return value != null && String(value).trim() !== ""
        ? [String(value).trim()]
        : [];
}

/**
 * Normalize any Firestore-timestamp-ish value into a real Timestamp (or null).
 * Handles an existing Timestamp, the client SDK `{ seconds }` shape, and the
 * Admin/Cloud-Function serialized `{ _seconds }` shape — so the app sees one
 * consistent shape regardless of which fetch path the data arrived through.
 * @param {*} value
 * @returns {Timestamp | null}
 */
export function toTimestamp(value) {
    if (!value) return null;
    if (value instanceof Timestamp) return value;
    const seconds = value.seconds ?? value._seconds ?? null;
    if (seconds == null || Number.isNaN(Number(seconds))) return null;
    return Timestamp.fromMillis(Number(seconds) * 1000);
}

export function extractTimestamp(timestamp) {
    if (!timestamp || !(timestamp instanceof Timestamp)) {
        // console.error("Invalid Firestore Timestamp provided.");
        return null;
    }
    const date = timestamp.toDate();
    const year = date.getFullYear();
    const month = date.toLocaleString("en-US", { month: "short" });
    const day = date.getDate();
    const hour = date.getHours();
    const minute = date.getMinutes();
    return {
        day: day,
        month: month,
        year: year,
        hour: hour,
        minute: minute,
    };
}

/**
 * Converts a Firestore Timestamp to a formatted date string (YYYY-MMM-DD).
 * @param {Timestamp} timestamp - Firestore Timestamp object.
 * @returns {string} Formatted date string in YYYY-MMM-DD format.
 */
export function formatFirestoreTimestamp(timestamp) {
    if (!timestamp || !(timestamp instanceof Timestamp)) {
        // console.error("Invalid Firestore Timestamp provided.");
        return null;
    }

    const date = timestamp.toDate(); // Convert to JavaScript Date object
    const year = date.getFullYear();
    const month = date.toLocaleString("en-US", { month: "short" }); // e.g., Jan, Feb, Mar
    const day = String(date.getDate()).padStart(2, "0"); // Ensure two-digit day format

    return `${month} ${day}, ${year}`;
}

/**
 * Converts a Firestore Timestamp to a formatted date string (HH:MM:PP).
 * @param {Timestamp} timestamp - Firestore Timestamp object.
 * @returns {string} Formatted date string to time
 */
export function formatFirestoreTimestampToTime(timestamp) {
    if (!timestamp || !(timestamp instanceof Timestamp)) {
        // console.error("Invalid Firestore Timestamp provided.");
        return null;
    }

    const date = timestamp.toDate(); // Convert to JavaScript Date object
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const period = hours < 12 ? "AM" : "PM";

    // Convert 24-hour format to 12-hour format
    hours = hours % 12;
    hours = hours ? hours : 12;

    const formattedMinutes = String(minutes).padStart(2, "0");

    return `${hours}:${formattedMinutes}${period}`;
}

/**
 * Gets the effective weight class for an event fighter.
 * Returns eventWeightClass if available, otherwise falls back to fighter.weightClass.
 *
 * @param {Object} eventFighter - The event fighter object with optional eventWeightClass and fighter property.
 * @returns {string|null} The effective weight class or null if not available.
 */
export function getEventFighterWeightClass(eventFighter) {
    if (!eventFighter) return null;
    return (
        eventFighter.eventWeightClass ??
        eventFighter.fighter?.weightClass ??
        null
    );
}

/**
 * Split a weight-class label into its pound and kilogram halves.
 *
 * `weightClasses` holds one string per class — "Bantamweight 135 lb (61.2 kg)"
 * — which reads fine on one line but gives the kg figure no way to carry its
 * own emphasis. The class name is dropped from the result because every caller
 * already shows it on its own line.
 *
 * Both halves can legitimately come back empty: class A has no pound figure,
 * and class K reads "> 120 kg" rather than an exact limit.
 *
 * @param {string} key A–K
 * @returns {{lb: string, kg: string}}
 */
export function splitWeightClassLabel(key) {
    const label = weightClasses[key];
    if (!label) return { lb: "", kg: "" };

    const match = label.match(/^(.*?)\s*\(([^)]*)\)\s*$/);
    if (!match) return { lb: label, kg: "" };

    const name = weightClassesName[key] ?? "";
    const head = match[1].trim();
    return {
        lb: head.startsWith(name) ? head.slice(name.length).trim() : head,
        kg: match[2].trim(),
    };
}

/**
 * Returns an array of recommended opponents for the given fighter.
 * Each opponent object includes a 'score' that reflects the similarity in
 * weight class, age, and height. The closer these attributes are, the higher the score.
 *
 * @param {Object} baseFighter - The fighter for whom to find similar opponents. Can be a fighter object or event fighter object.
 * @param {Array} fighters - Array of fighter objects or event fighter objects.
 * @param {string} entry - The entry/competition type.
 * @param {Function} getWeightClassFn - Optional function to get weight class. If provided, used instead of fighter.weightClass.
 * @returns {Array} Array of fighter objects (with a 'score' property) sorted by score.
 */
export function getRecommendedOpponents(
    baseFighter,
    fighters,
    entry,
    getWeightClassFn = null
) {
    // const weightMap = {
    //     A: 1,
    //     B: 2,
    //     C: 3,
    //     D: 4,
    //     E: 5,
    //     F: 6,
    //     G: 7,
    //     H: 8,
    //     I: 9,
    //     J: 10,
    //     K: 11,
    // };

    // Helper function to get weight class
    const getWeightClass = (fighter) => {
        if (getWeightClassFn) {
            return getWeightClassFn(fighter);
        }
        return fighter.weightClass ?? fighter.fighter?.weightClass ?? null;
    };

    const baseFighterId = baseFighter.id ?? baseFighter.fighterId;
    const baseWeightClass = getWeightClass(baseFighter);
    const baseGender = baseFighter.gender ?? baseFighter.fighter?.gender;

    return fighters
        .filter((f) => {
            const fId = f.id ?? f.fighterId;
            return fId !== baseFighterId;
        })
        .map((f) => {
            //! Weight must be same
            //! Gender must be same
            //! Entry must be same
            const fWeightClass = getWeightClass(f);
            if (baseWeightClass !== fWeightClass) {
                return { ...f, score: -Infinity, reason: "Diff. Weight Class" };
            }

            const fGender = f.gender ?? f.fighter?.gender;
            if (baseGender !== fGender) {
                return { ...f, score: -Infinity, reason: "Diff. Gender" };
            }

            // Calculate differences
            const baseAge = baseFighter.age ?? baseFighter.fighter?.age ?? 0;
            const fAge = f.age ?? f.fighter?.age ?? 0;
            const ageDiff = Math.abs(baseAge - fAge);

            const baseHeight =
                baseFighter.height ?? baseFighter.fighter?.height ?? 0;
            const fHeight = f.height ?? f.fighter?.height ?? 0;
            const heightDiff = Math.abs(baseHeight - fHeight);

            const baseRecords =
                baseFighter.records ?? baseFighter.fighter?.records ?? {};
            const fRecords = f.records ?? f.fighter?.records ?? {};
            const baseEntryRecord = baseRecords[entry] ?? {
                win: 0,
                loss: 0,
                draw: 0,
            };
            const fEntryRecord = fRecords[entry] ?? {
                win: 0,
                loss: 0,
                draw: 0,
            };

            const winDiff = Math.abs(
                (baseEntryRecord.win ?? 0) - (fEntryRecord.win ?? 0)
            );
            const lossDiff = Math.abs(
                (baseEntryRecord.loss ?? 0) - (fEntryRecord.loss ?? 0)
            );
            const drawDiff = Math.abs(
                (baseEntryRecord.draw ?? 0) - (fEntryRecord.draw ?? 0)
            );

            //! Win - 5
            //! Loss - 5
            //! Draw - 5
            //! Age - 3
            //! Height - 1
            const winFactor = 5;
            const lossFactor = 5;
            const drawFactor = 5;
            const ageFactor = 3;
            const heightFactor = 1;

            //Record , weight, gender
            //Total record for match fighter

            const penalty =
                ageDiff * ageFactor +
                heightDiff * heightFactor +
                winDiff * winFactor +
                lossDiff * lossFactor +
                drawDiff * drawFactor;

            const score = 100 - penalty;
            return { ...f, score };
        })
        .sort((a, b) => b.score - a.score);
}

/**
 * Calculates the age in years given a birth date.
 *
 * @param {Date|string|number} birthDate - The birth date. Can be a Date object,
 *                                         a date string, or a timestamp.
 * @returns {number} The age in years.
 */
export function calculateAge(birthDate) {
    const today = new Date();
    const date = new Date(birthDate);
    let age = today.getFullYear() - date.getFullYear();
    const monthDiff = today.getMonth() - date.getMonth();

    if (
        monthDiff < 0 ||
        (monthDiff === 0 && today.getDate() < date.getDate())
    ) {
        age--;
    }
    return age;
}

/**
 * Coerce any of the date shapes we deal with (JS Date, ISO/number, a Firestore
 * Timestamp instance, or a callable-serialized {_seconds}/{seconds} object)
 * into a JS Date. Returns null when it can't.
 *
 * @param {*} value
 * @returns {Date|null}
 */
export function toJsDate(value) {
    if (!value) return null;
    if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
    if (typeof value === "number" || typeof value === "string") {
        const d = new Date(value);
        return isNaN(d.getTime()) ? null : d;
    }
    if (typeof value.toDate === "function") {
        try {
            return value.toDate();
        } catch {
            return null;
        }
    }
    if (typeof value.seconds === "number") return new Date(value.seconds * 1000);
    if (typeof value._seconds === "number")
        return new Date(value._seconds * 1000);
    return null;
}

/**
 * Age in whole years as of a reference date (defaults to today).
 *
 * @param {*} birthDate - any shape toJsDate accepts
 * @param {*} [atDate]  - reference date; defaults to now
 * @returns {number|null} age in years, or null if birthDate is unparseable
 */
export function getAgeAtDate(birthDate, atDate) {
    const date = toJsDate(birthDate);
    if (!date) return null;
    const ref = atDate ? toJsDate(atDate) : new Date();
    if (!ref) return null;
    let age = ref.getFullYear() - date.getFullYear();
    const monthDiff = ref.getMonth() - date.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && ref.getDate() < date.getDate())) {
        age--;
    }
    return age;
}

/**
 * Whether a fighter is a minor (`threshold` years old or younger, default 17)
 * as of a given date. Used to flag under-age fighters at the event date.
 * Returns false when the birth date is missing/unparseable (not flagged).
 *
 * @param {*} birthDate
 * @param {*} [atDate] - reference date; defaults to now
 * @param {number} [threshold=17]
 * @returns {boolean}
 */
export function isMinorAtDate(birthDate, atDate, threshold = 17) {
    const age = getAgeAtDate(birthDate, atDate);
    if (age === null) return false;
    return age <= threshold;
}

/**
 * Calculates the latest valid birth date for a given minimum age.
 * In other words, if the minimum age requirement is X years,
 * this function returns the date that a person must be born on (or before)
 * to be at least X years old as of today.
 *
 * @param {number} minAge - The minimum age required.
 * @returns {Date} The latest birth date allowed.
 */
export function calculateMinDateByAge(age) {
    const today = new Date();
    // Subtract minAge years from today's date.
    return new Date(
        today.getFullYear() - age,
        today.getMonth(),
        today.getDate()
    );
}

/**
 * Asynchronously checks if the current device is running on a Mac.
 * @returns {Promise<boolean>} A promise that resolves to true if on Mac, false otherwise.
 */
export function isMac() {
    if (navigator.userAgentData) {
        // Use high entropy values from userAgentData
        return navigator.userAgentData
            .getHighEntropyValues(["platform"])
            .then((ua) => {
                return ua.platform.toLowerCase().includes("mac");
            })
            .catch((error) => {
                console.error("Error fetching high entropy values:", error);
                // Fallback to using navigator.userAgent
                return navigator.userAgent.toLowerCase().includes("mac");
            });
    } else {
        // Fallback for browsers that do not support navigator.userAgentData
        return Promise.resolve(
            navigator.userAgent.toLowerCase().includes("mac")
        );
    }
}

export const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
        2,
        "0"
    )}:${String(secs).padStart(2, "0")}`;
};

export const splitCamel = (str) => {
    return (
        str
            // Add space before all caps following lowercase, or lowercase after acronyms
            .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
            .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
            .replace(/^./, (s) => s.toUpperCase())
    );
};

export const getUniqueMatchedFighters = (eventFighters) => {
    if (!eventFighters) return [];

    const matchedPairs = new Set();
    const uniqueMatchedFighters = {};

    eventFighters.forEach((eventFighter) => {
        if (eventFighter.matchWith) {
            // Create a unique key for the pair (sorted to ensure A-B and B-A are treated as the same)
            const pairKey = [eventFighter.fighterId, eventFighter.matchWith]
                .sort()
                .join("-");

            // Only add if we haven't seen this pair before
            if (!matchedPairs.has(pairKey)) {
                matchedPairs.add(pairKey);
                const a = eventFighter;
                const b = eventFighters.find(
                    (ef) => ef.fighterId === eventFighter.matchWith
                );
                uniqueMatchedFighters[pairKey] = {
                    winId: null, //is fighter Id
                    lossId: null, //is fighter Id
                    isDraw: false,
                    fighterA: a,
                    fighterB: b,
                };
            }
        }
    });

    return uniqueMatchedFighters;
};

export const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "MYR",
    }).format(value);
};



// format numbder for the thousdanrt and more to add comma. 
export function formatNumberWithCommas(number) {
    return new Intl.NumberFormat("en-US").format(number);
}

/**
 * Determines the optimal text color (black or white) for a given background color.
 * Uses the WCAG relative luminance formula for accessibility.
 * 
 * @param {string} hexColor - The background color in hex format (with or without #)
 * @returns {string} Either '#000000' (black) or '#FFFFFF' (white)
 */
export function getContrastTextColor(hexColor) {
    if (!hexColor) return '#000000';
    
    // Remove # if present
    const hex = hexColor.replace('#', '');
    
    // Handle invalid hex colors
    if (!/^[0-9A-Fa-f]{6}$/.test(hex)) {
        return '#000000';
    }
    
    // Convert to RGB
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    // Calculate relative luminance using WCAG formula
    const [rs, gs, bs] = [r, g, b].map(c => {
        c = c / 255;
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    
    const luminance = 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
    
    // Return black for light backgrounds (luminance > 0.5), white for dark
    return luminance > 0.5 ? '#000000' : '#FFFFFF';
}

/**
 * Formats a phone number for WhatsApp URL usage.
 * Handles various formats and ensures Malaysia country code (60) is present.
 * 
 * Examples:
 * - "012-785 4495" -> "60127854495"
 * - "+60127854495" -> "60127854495"
 * - "60127854495" -> "60127854495"
 * - "12-785 4495" -> "60127854495"
 * - "012 785 4495" -> "60127854495"
 * - "+60 12-785 4495" -> "60127854495"
 * 
 * @param {string} phoneNumber - The phone number to format (can contain spaces, dashes, dots, parentheses, etc.)
 * @returns {string} Formatted phone number with Malaysia country code (60), without + sign or special characters
 */
export function formatPhoneNumberForWhatsApp(phoneNumber) {
    if (!phoneNumber) return '';
    
    // Convert to string and remove all non-digit characters (spaces, dashes, dots, parentheses, +, etc.)
    let cleaned = String(phoneNumber).replace(/\D/g, '');
    
    // Return empty string if no digits found
    if (!cleaned) return '';
    
    // If it already starts with Malaysia country code (60), return as is
    if (cleaned.startsWith('60')) {
        return cleaned;
    }
    
    // If it starts with 0 (Malaysian local format), replace 0 with 60
    if (cleaned.startsWith('0')) {
        return '60' + cleaned.substring(1);
    }
    
    // If it's a 9-10 digit number (typical Malaysian mobile number without country code),
    // assume it's Malaysian and add country code 60
    // Malaysian mobile numbers are typically 10 digits (including leading 0) or 9 digits (without 0)
    if (cleaned.length >= 9 && cleaned.length <= 10) {
        return '60' + cleaned;
    }
    
    // For other cases (already has different country code, or unusual format),
    // return as is (assume it's already properly formatted)
    return cleaned;
}